/**
 * Scale an SVG path `d=` attribute from slot-local 0-1 unit coords to
 * pixel-space coords (multiplied by bboxW / bboxH per axis).
 *
 * Handles every SVG path command Canva exports:
 *   M m  L l  H h  V v  C c  S s  Q q  T t  A a  Z z
 *
 * Relative commands (lowercase) keep their case so the path renders the
 * same after scaling — relative deltas in unit space scale the same as
 * absolute coords. Flags inside A/a (large-arc, sweep) pass through.
 */
export function scalePath(d: string, bboxW: number, bboxH: number): string {
  // Tokenize: commands are single letters, numbers are signed floats.
  const tokens = d.match(/[a-zA-Z]|-?\d*\.?\d+(?:[eE][-+]?\d+)?/g);
  if (!tokens) return d;

  let cmd = '';
  let i = 0;
  const out: string[] = [];

  while (i < tokens.length) {
    const t = tokens[i];
    if (/^[a-zA-Z]$/.test(t)) {
      cmd = t;
      out.push(t);
      i++;
      continue;
    }
    // Numeric token under the current command — scale per axis.
    switch (cmd) {
      case 'M': case 'm': case 'L': case 'l':
      case 'C': case 'c': case 'S': case 's':
      case 'Q': case 'q': case 'T': case 't': {
        // Pairs of (x, y). Read 2 numbers, scale by W, H.
        const x = parseFloat(tokens[i]);
        const y = parseFloat(tokens[i + 1]);
        out.push(String(x * bboxW));
        out.push(String(y * bboxH));
        i += 2;
        break;
      }
      case 'H': case 'h': {
        out.push(String(parseFloat(tokens[i]) * bboxW));
        i++;
        break;
      }
      case 'V': case 'v': {
        out.push(String(parseFloat(tokens[i]) * bboxH));
        i++;
        break;
      }
      case 'A': case 'a': {
        // rx ry x-axis-rotation large-arc-flag sweep-flag x y
        const rx = parseFloat(tokens[i]);
        const ry = parseFloat(tokens[i + 1]);
        const xRot = tokens[i + 2];      // pass through
        const largeArc = tokens[i + 3];  // pass through (flag)
        const sweep = tokens[i + 4];     // pass through (flag)
        const x = parseFloat(tokens[i + 5]);
        const y = parseFloat(tokens[i + 6]);
        out.push(
          String(rx * bboxW),
          String(ry * bboxH),
          xRot,
          largeArc,
          sweep,
          String(x * bboxW),
          String(y * bboxH),
        );
        i += 7;
        break;
      }
      case 'Z': case 'z':
        // No coordinates after Z; skip — shouldn't reach here.
        i++;
        break;
      default:
        // Unknown command — pass token through verbatim.
        out.push(tokens[i]);
        i++;
    }
  }
  return out.join(' ').replace(/\s+/g, ' ').trim();
}
